/* ===================== SETTINGS / MY PROFILE (seeker) =====================
   Live profile editor. Prefill: GET /user (same query key as useDashboard,
   so the read is shared). Save: PUT /user/profile — upstream validates it
   as a FULL update (first_name, email, mobile_no, country_id required on
   every call), so those always ship even when only one field changed.
   Laravel 422 field errors surface inline under their inputs.

   Country/State/City are react-selects fed by the location lookup APIs
   (GET /get-country-list → POST /get-state-list → POST /get-city-list).
   Country submits its numeric id; state/city submit plain name strings —
   that's what the profile validator accepts — so both are creatable in
   case the lookup list misses a value.

   Birth details (dob, time_of_birth, is_birth_time_unknown) are editable
   here and feed the Kundli tab's prefill; City doubles as the place of birth,
   so place_of_birth isn't sent (the API keeps whatever the mobile app saved).
   The API only touches those keys when they're sent, so they always ship —
   blank clears one, and "time unknown" clears the stored time.

   The profile photo uploads on pick (POST /user/profile/photo, multipart
   `photo`), independent of the form's Save. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { FiCamera } from 'react-icons/fi'
import { api, INDIA_COUNTRY_ID } from '@astro/api'
import { useAuth } from '@astro/auth'
import { Controller, useForm } from '@astro/forms'
import { DateField, TimeField } from '@astro/date-field'
import { selectStyles, toList } from '@astro/astrologer-forms'
import { initials, normaliseTime } from '../useDashboard'
import ChangePasswordCard from './ChangePasswordCard'

/** Unwrap a Laravel `{ status, data }` envelope (or pass a bare value through). */
function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res) return res.data
  return res
}

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

function Field({ label, error, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/** Form-bound react-select in the wizard's skin. Non-creatable selects only
 * show a value once it matches an option (country id → name); creatable ones
 * render whatever string the profile carries even if the lookup misses it. */
function SelectInput({ control, name, rules, options, placeholder, isLoading, isDisabled, creatable, onChangeValue }) {
  const Cmp = creatable ? CreatableSelect : Select
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <Cmp
          inputId={`profile-${name}`}
          options={options}
          value={
            options.find((o) => String(o.value) === String(field.value)) ??
            (creatable && field.value ? { value: field.value, label: String(field.value) } : null)
          }
          onChange={(option) => {
            const value = option?.value ?? ''
            field.onChange(value)
            onChangeValue?.(value)
          }}
          onBlur={field.onBlur}
          placeholder={placeholder}
          styles={selectStyles}
          isClearable={Boolean(creatable)}
          isLoading={isLoading}
          isDisabled={isDisabled}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          formatCreateLabel={(input) => `Use "${input}"`}
        />
      )}
    />
  )
}

// Mirrors the API's rules, so a bad pick fails here instead of after the upload.
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const PHOTO_MAX_BYTES = 5 * 1024 * 1024

/** Avatar + upload/remove controls. The response is the updated /user
 * record, so it's written straight into the shared ['dashboard','me'] cache
 * (sidebar avatar updates at once); reloadSession() catches the headers up. */
function ProfilePhoto({ photoUrl, name }) {
  const queryClient = useQueryClient()
  const { reloadSession } = useAuth()
  const inputRef = useRef(null)
  // Local preview of the picked file. Kept after a successful upload — it's
  // the same image — so the avatar doesn't blank while the stored URL loads.
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview])

  const onSaved = (res) => {
    queryClient.setQueryData(['dashboard', 'me'], res)
    reloadSession?.()
  }
  const onFailed = (err) => {
    setPreview(null)
    const details = err?.error?.details
    setError(
      (Array.isArray(details?.photo) && details.photo[0]) ||
        err?.error?.message ||
        err?.message ||
        'Could not update your photo. Please try again.',
    )
  }

  const upload = useMutation({ mutationFn: (file) => api.dashboard.uploadProfilePhoto(file), onSuccess: onSaved, onError: onFailed })
  const remove = useMutation({
    mutationFn: () => api.dashboard.removeProfilePhoto(),
    onSuccess: (res) => {
      setPreview(null)
      onSaved(res)
    },
    onError: onFailed,
  })
  const busy = upload.isPending || remove.isPending

  const onPick = (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // let the same file be picked again after an error
    if (!file) return
    if (!PHOTO_TYPES.includes(file.type)) return setError('Please choose a JPG, PNG or WebP image.')
    if (file.size > PHOTO_MAX_BYTES) return setError('The photo must not be larger than 5 MB.')
    setError(null)
    setPreview(URL.createObjectURL(file))
    upload.mutate(file)
  }

  const src = preview || photoUrl
  const openPicker = () => inputRef.current?.click()

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 pb-6 mb-6 border-b border-stone-100">
      <button
        type="button"
        onClick={openPicker}
        disabled={busy}
        aria-label={photoUrl ? 'Change profile photo' : 'Upload profile photo'}
        className="group relative shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-[#3D0A0A] focus:outline-none focus-visible:ring-2 focus-visible:ring-goldAccent/60"
      >
        {src ? (
          <img src={src} alt={name} className="w-full h-full object-cover object-top" />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-[#D4A017] font-bold text-2xl sm:text-3xl">{initials(name)}</span>
        )}
        <span
          className={`absolute inset-0 flex items-center justify-center bg-black/45 transition-opacity ${busy ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          {busy ? (
            <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <FiCamera className="w-5 h-5 text-white" />
          )}
        </span>
      </button>

      <div className="min-w-0">
        <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1">Profile Photo</p>
        <p className="text-xs text-stone-500 mb-3">JPG, PNG or WebP, up to 5 MB.</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openPicker}
            disabled={busy}
            className="bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.18em] px-4 py-2.5 rounded-xl shadow-sm shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {upload.isPending ? 'Uploading…' : photoUrl ? 'Change Photo' : 'Upload Photo'}
          </button>
          {photoUrl && (
            <button
              type="button"
              onClick={() => {
                setError(null)
                remove.mutate()
              }}
              disabled={busy}
              className="text-[10px] font-bold uppercase tracking-[0.18em] px-4 py-2.5 rounded-xl border transition-colors disabled:opacity-60"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </button>
          )}
        </div>
        {error && <p className="text-[10px] text-red-500 mt-2">{error}</p>}
        <input ref={inputRef} type="file" accept={PHOTO_TYPES.join(',')} className="hidden" onChange={onPick} />
      </div>
    </div>
  )
}

function FormSkeleton() {
  return (
    <div className="dash-card p-4 sm:p-6 animate-pulse" aria-busy="true" aria-label="Loading your profile">
      <div className="h-4 w-44 bg-stone-100 rounded-full mb-2" />
      <div className="h-2.5 w-64 bg-stone-100 rounded-full mb-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-2 w-24 bg-stone-100 rounded-full" />
            <div className="h-11 bg-stone-100 rounded-xl" />
          </div>
        ))}
        <div className="sm:col-span-2 flex justify-end pt-4 border-t border-stone-100">
          <div className="h-11 w-36 bg-stone-100 rounded-xl" />
        </div>
      </div>
    </div>
  )
}

/** Coerce whatever date string the profile carries into <input type="date"> form. */
const toDateInput = (s) => {
  if (!s) return ''
  const str = String(s)
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10)
  const d = new Date(str)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

/** Prefill straight off the raw /user record; name splits when the payload
 * only carries a combined `name`. */
function buildPrefill(p = {}) {
  const parts = String(p.name || '').trim().split(/\s+/).filter(Boolean)
  return {
    first_name: p.first_name || parts[0] || '',
    middle_name: p.middle_name || '',
    last_name: p.last_name || parts.slice(1).join(' ') || '',
    email: p.email || '',
    mobile_no: p.mobile_no || p.mobile || '',
    alt_ph: p.alt_ph || '',
    dob: toDateInput(p.dob || p.date_of_birth),
    country_id: Number(p.country_id) || INDIA_COUNTRY_ID,
    state: p.state || '',
    city: p.city || '',
    address: p.address || '',
    pincode: p.pincode || p.pin_code || '',
    // Stored as entered — the mobile app saves "2:35 PM" — so normalise to the
    // 24h 'HH:mm' TimeField works in.
    time_of_birth: normaliseTime(p.time_of_birth || p.birth_time) || '',
    is_birth_time_unknown: Boolean(p.is_birth_time_unknown),
  }
}

/** PUT /user/profile payload — required fields always present, optional
 * fields only when non-empty (undefined keys drop out of the JSON body).
 * Middle name and birth details always ship (null = clear): the API keeps
 * any of those keys that are left out. */
function buildPayload(v) {
  const opt = (s) => {
    const t = String(s ?? '').trim()
    return t || undefined
  }
  return {
    first_name: v.first_name.trim(),
    email: v.email.trim(),
    mobile_no: v.mobile_no.trim(),
    country_id: Number(v.country_id) || INDIA_COUNTRY_ID,
    middle_name: opt(v.middle_name) ?? null,
    last_name: opt(v.last_name),
    alt_ph: opt(v.alt_ph),
    dob: opt(v.dob) ?? null,
    time_of_birth: v.is_birth_time_unknown ? null : opt(v.time_of_birth) ?? null,
    is_birth_time_unknown: Boolean(v.is_birth_time_unknown),
    state: opt(v.state),
    city: opt(v.city),
    address: opt(v.address),
    pincode: opt(v.pincode),
  }
}

export default function SettingsTab() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null) // { tone: 'ok' | 'err', text }

  const meQuery = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.dashboard.me(),
    enabled: Boolean(user),
    retry: false,
    select: unwrap,
  })

  const me = meQuery.data || {}
  const prefill = useMemo(() => buildPrefill(me), [meQuery.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: prefill,
    values: prefill,
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched',
  })

  /* ── location reference data: country → states → cities ── */
  const countryId = watch('country_id')
  const stateName = watch('state')

  const countriesQuery = useQuery({
    queryKey: ['lookups', 'countries'],
    queryFn: () => api.lookups.countries(),
    retry: false,
    staleTime: Infinity,
    select: (res) => toList(res).filter((c) => c?.id != null),
  })
  const countries = countriesQuery.data ?? []
  const countryOptions = countries.length
    ? countries.map((c) => ({ value: c.id, label: c.name || c.country_name || `Country ${c.id}` }))
    : [{ value: INDIA_COUNTRY_ID, label: 'India' }]

  const statesQuery = useQuery({
    queryKey: ['lookups', 'states', countryId],
    queryFn: () => api.lookups.states(countryId),
    enabled: Boolean(countryId),
    retry: false,
    staleTime: Infinity,
    select: (res) => toList(res).filter((s) => s?.id != null),
  })
  const states = statesQuery.data ?? []
  // The profile stores the state as a name string — match it back to the
  // lookup row to get the id the city listing needs.
  const selectedState = states.find((s) => String(s.name).toLowerCase() === String(stateName || '').toLowerCase())

  const citiesQuery = useQuery({
    queryKey: ['lookups', 'cities', selectedState?.id],
    queryFn: () => api.lookups.cities(selectedState.id),
    enabled: selectedState != null,
    retry: false,
    staleTime: Infinity,
    select: (res) => toList(res).filter((c) => c?.id != null),
  })
  const cities = citiesQuery.data ?? []

  const saveMutation = useMutation({
    mutationFn: (payload) => api.dashboard.updateProfile(payload),
    onSuccess: () => {
      setBanner({ tone: 'ok', text: 'Profile saved.' })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'me'] })
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; field names
      // match the payload keys 1:1, so they surface inline as-is.
      const details = err?.error?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner({ tone: 'err', text: err?.error?.message || err?.message || 'Could not save your profile. Please try again.' })
    },
  })

  const onSave = handleSubmit(
    (values) => {
      setBanner(null)
      saveMutation.mutate(buildPayload(values))
    },
    () => setBanner({ tone: 'err', text: 'Please fix the highlighted fields before saving.' }),
  )

  if (meQuery.isPending) return <FormSkeleton />

  if (meQuery.isError) {
    return (
      <>
      <div className="dash-card p-4 sm:p-6 py-12 text-center">
        <div className="text-4xl mb-3 opacity-40">🛰️</div>
        <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t load your profile</p>
        <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching your account details.</p>
        <button
          type="button"
          onClick={() => meQuery.refetch()}
          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
          style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
        >
          Retry
        </button>
      </div>
      <ChangePasswordCard />
      </>
    )
  }

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`
  const birthTimeUnknown = watch('is_birth_time_unknown')

  return (
    <>
    <div className="dash-card p-4 sm:p-6">
      <h3 className="text-xl text-astroDark mb-1 font-royal">Account Settings</h3>
      <p className="text-xs text-stone-500 mb-6">Update your personal and contact details — changes apply across all Astro Rajumaharaj apps.</p>
      <ProfilePhoto
        photoUrl={me.profile_image || null}
        name={[me.first_name, me.last_name].filter(Boolean).join(' ') || me.name || user?.name}
      />
      <form onSubmit={onSave} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="First Name" error={errors.first_name?.message}>
          <input className={cls('first_name')} {...register('first_name', { required: 'First name is required' })} />
        </Field>
        <Field label="Middle Name" error={errors.middle_name?.message}>
          <input className={cls('middle_name')} {...register('middle_name')} />
        </Field>
        <Field label="Last Name" error={errors.last_name?.message}>
          <input className={cls('last_name')} {...register('last_name')} />
        </Field>
        <Field label="Date of Birth" error={errors.dob?.message}>
          <Controller
            name="dob"
            control={control}
            render={({ field }) => (
              <DateField
                max={new Date().toISOString().slice(0, 10)}
                className={cls('dob')}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        </Field>
        <Field label="Email Address" error={errors.email?.message}>
          <input
            type="email"
            className={cls('email')}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
        </Field>
        <Field label="Mobile Number" error={errors.mobile_no?.message}>
          <input
            inputMode="numeric"
            maxLength={10}
            className={cls('mobile_no')}
            {...register('mobile_no', {
              required: 'Mobile number is required',
              pattern: { value: /^[6-9]\d{9}$/, message: 'Enter the 10-digit mobile number' },
            })}
          />
        </Field>
        <Field label="Alternate Phone" error={errors.alt_ph?.message}>
          <input
            inputMode="numeric"
            maxLength={10}
            className={cls('alt_ph')}
            {...register('alt_ph', {
              validate: (v) => !v || /^[6-9]\d{9}$/.test(v) || 'Enter a valid 10-digit number',
            })}
          />
        </Field>
        <Field label="Country" error={errors.country_id?.message}>
          <SelectInput
            control={control}
            name="country_id"
            rules={{ required: 'Country is required' }}
            options={countryOptions}
            placeholder="Select country"
            isLoading={countriesQuery.isLoading}
            onChangeValue={() => {
              setValue('state', '')
              setValue('city', '')
            }}
          />
        </Field>
        <Field label="State" error={errors.state?.message}>
          <SelectInput
            control={control}
            name="state"
            creatable
            options={states.map((s) => ({ value: s.name, label: s.name }))}
            placeholder={countryId ? 'Select or type state' : 'Select country first'}
            isDisabled={!countryId}
            isLoading={Boolean(countryId) && statesQuery.isLoading}
            onChangeValue={() => setValue('city', '')}
          />
        </Field>
        <Field label="City" error={errors.city?.message}>
          <SelectInput
            control={control}
            name="city"
            creatable
            options={cities.map((c) => ({ value: c.name, label: c.name }))}
            placeholder={stateName ? 'Select or type city' : 'Select state first'}
            isDisabled={!stateName}
            isLoading={selectedState != null && citiesQuery.isLoading}
          />
        </Field>
        <Field label="Address" error={errors.address?.message}>
          <input className={cls('address')} {...register('address')} />
        </Field>
        <Field label="Pincode" error={errors.pincode?.message}>
          <input
            inputMode="numeric"
            maxLength={6}
            className={cls('pincode')}
            {...register('pincode', {
              validate: (v) => !v || /^\d{6}$/.test(v) || 'Enter a 6-digit pincode',
            })}
          />
        </Field>

        <div className="sm:col-span-2 pt-4 border-t border-stone-100">
          <p className="text-sm text-astroDark font-semibold">Birth Details</p>
          <p className="text-[11px] text-stone-500">Used to prefill your Kundli and shared with astrologers you consult. Your City above is taken as your place of birth.</p>
        </div>
        <Field label="Time of Birth" error={errors.time_of_birth?.message}>
          <Controller
            name="time_of_birth"
            control={control}
            render={({ field }) => (
              <TimeField
                name="time_of_birth"
                disabled={birthTimeUnknown}
                placeholder={birthTimeUnknown ? 'Not known' : 'hh : mm am/pm'}
                className={`${cls('time_of_birth')} ${birthTimeUnknown ? 'opacity-60 cursor-not-allowed' : ''}`}
                value={birthTimeUnknown ? '' : field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-stone-300 accent-amber-600"
              {...register('is_birth_time_unknown', {
                onChange: (e) => e.target.checked && setValue('time_of_birth', '', { shouldDirty: true }),
              })}
            />
            <span className="text-xs text-stone-500">I don&rsquo;t know my exact birth time</span>
          </label>
        </Field>

        <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-stone-100">
          <div className="min-w-0">
            {banner ? (
              <p className={`text-xs font-semibold ${banner.tone === 'ok' ? 'text-green-600' : 'text-red-500'}`}>{banner.text}</p>
            ) : (
              <p className="text-xs text-stone-500">Review your details, then save.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shrink-0"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>

    <ChangePasswordCard />
    </>
  )
}
